import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CircleCheck, ClipboardCheck, ExternalLink, ScanBarcode, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AssetStatusBadge } from '../components/badges';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Table, Td, Textarea, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { formatRelative, formatUtcDate } from '../lib/format';
import { invalidateInventory, useBuildings } from '../lib/hooks';
import { localToday } from '../lib/locations';
import type { AssetListItem, Checkout } from '../lib/types';
import type { VerifyPlace, VerifyResult } from '../lib/verifications';

type Scanned = Omit<AssetListItem, 'activeCheckout'> & { activeCheckout: Checkout | null };

/** Where a device is kept today, as the list shows it. */
function homeLabel(asset: Scanned) {
  if (asset.cart) return `${asset.cart.name} (cart)`;
  return [asset.room?.name, asset.building?.name].filter(Boolean).join(' · ') || 'No home location';
}

const devices = (count: number) => `${count} device${count === 1 ? '' : 's'}`;
const cartNote = (count: number, tense: 'stay' | 'stayed') =>
  `${count} ${tense === 'stay' ? (count === 1 ? 'stays' : 'stay') : 'stayed'} with ${count === 1 ? 'its' : 'their'} cart`;

export function VerifyAssetsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const buildings = useBuildings();
  const scanRef = useRef<HTMLInputElement>(null);

  const [buildingId, setBuildingId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState('');
  const [scanned, setScanned] = useState<Scanned[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [nextOn, setNextOn] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<VerifyResult | null>(null);
  // The list as it stands, for scans that finish after the page last rendered.
  const scannedRef = useRef(scanned);

  useEffect(() => {
    scannedRef.current = scanned;
  });

  const rooms = buildings.data?.find((building) => building.id === buildingId)?.rooms ?? [];
  const chosen = !!buildingId;

  const place = useQuery({
    queryKey: ['verify-place', buildingId, roomId],
    queryFn: () => api.get<VerifyPlace>('/verifications/place', { buildingId, roomId }),
    enabled: chosen,
  });

  const focusScan = () => setTimeout(() => scanRef.current?.focus(), 0);
  useEffect(() => {
    if (chosen) focusScan();
  }, [chosen, roomId]);

  const lookup = useMutation({
    mutationFn: (code: string) => api.get<Scanned>('/assets/lookup', { q: code }),
    onSuccess: (asset) => {
      setScan('');
      setScanError('');
      if (scannedRef.current.some((row) => row.id === asset.id)) {
        toast.success(`${asset.assetTag} is already on the list`);
      } else {
        setScanned((current) => (current.some((row) => row.id === asset.id) ? current : [...current, asset]));
      }
      focusScan();
    },
    onError: (err) => {
      setScan('');
      setScanError(errorMessage(err));
      focusScan();
    },
  });

  const verify = useMutation({
    mutationFn: () =>
      api.post<VerifyResult>('/verifications', {
        buildingId,
        roomId: roomId || undefined,
        assetIds: scanned.map((asset) => asset.id),
        nextVerificationOn: nextOn || undefined,
        notes: notes || undefined,
      }),
    onSuccess: (done) => {
      invalidateInventory(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['verify-place'] });
      void queryClient.invalidateQueries({ queryKey: ['asset-verifications'] });
      setConfirming(false);
      setResult(done);
      setScanned([]);
      setScanError('');
      setNotes('');
      setNextOn('');
      toast.success(`Verified ${devices(done.verified)} in ${done.label}`);
      focusScan();
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const submitScan = (event: FormEvent) => {
    event.preventDefault();
    const code = scan.trim();
    if (code) lookup.mutate(code);
  };

  const moves = (asset: Scanned) => !asset.cart && (asset.buildingId !== buildingId || (asset.roomId ?? '') !== roomId);
  const willMove = scanned.filter(moves).length;
  const onCarts = scanned.filter((asset) => !!asset.cart).length;

  return (
    <>
      <PageHeader
        title="Verify assets"
        description="Scan what’s actually in a room. Each device is recorded as verified, and the room becomes where it’s kept."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Where you are" description="Pick the building and room you’re standing in" />
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <Field label="Building" required>
                <Select
                  value={buildingId}
                  onChange={(event) => {
                    setBuildingId(event.target.value);
                    setRoomId('');
                    setResult(null);
                  }}
                >
                  <option value="">Choose a building</option>
                  {buildings.data?.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                      {building.kind === 'WAREHOUSE' ? ' (warehouse)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Room" hint={chosen && !roomId ? 'Devices kept in the building, but not in a room' : undefined}>
                <Select
                  value={roomId}
                  disabled={!chosen}
                  onChange={(event) => {
                    setRoomId(event.target.value);
                    setResult(null);
                  }}
                >
                  <option value="">No room (the building itself)</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <form onSubmit={submitScan} className="flex gap-2">
              <div className="relative flex-1">
                <ScanBarcode className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={scanRef}
                  value={scan}
                  disabled={!chosen}
                  onChange={(event) => setScan(event.target.value)}
                  placeholder={chosen ? 'Scan or type an asset tag / serial' : 'Choose where you are first'}
                  className="h-12 pl-11 font-mono text-base"
                  autoComplete="off"
                />
              </div>
              <Button type="submit" variant="primary" className="h-12 px-5" disabled={!chosen} loading={lookup.isPending}>
                Add
              </Button>
            </form>
            {scanError && (
              <Alert tone="amber" className="mt-3">
                {scanError}
              </Alert>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Scanned here"
              description={`${devices(scanned.length)}${willMove > 0 ? ` · ${willMove} will move here` : ''}${onCarts > 0 ? ` · ${onCarts} on a cart` : ''}`}
              actions={
                scanned.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setScanned([])}>
                    Clear
                  </Button>
                )
              }
            />
            {scanned.length === 0 ? (
              <EmptyState icon={<ScanBarcode />} title="Nothing scanned yet" description="Every scan adds the device to this list." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Asset</Th>
                    <Th>Device</Th>
                    <Th>Status</Th>
                    <Th>Assigned to</Th>
                    <Th>Kept in</Th>
                    <Th className="text-right">Remove</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {scanned.map((asset) => (
                    <tr key={asset.id} className="hover:bg-slate-50/60">
                      <Td>
                        <Link to={`/assets/${asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                          {asset.assetTag}
                        </Link>
                        {asset.serialNumber && <p className="text-xs text-slate-500">{asset.serialNumber}</p>}
                      </Td>
                      <Td className="max-w-xs">
                        <p className="truncate text-slate-900">{asset.name}</p>
                        {asset.category && <p className="truncate text-xs text-slate-500">{asset.category.name}</p>}
                      </Td>
                      <Td>
                        <AssetStatusBadge status={asset.status} />
                      </Td>
                      <Td className="max-w-xs">
                        {asset.activeCheckout ? (
                          <span className="truncate text-slate-800">{asset.activeCheckout.assigneeLabel}</span>
                        ) : (
                          <span className="text-slate-400">Nobody</span>
                        )}
                      </Td>
                      <Td className="max-w-xs">
                        <p className="truncate text-slate-700">{homeLabel(asset)}</p>
                        {moves(asset) && <Badge tone="amber">Moves here</Badge>}
                        {asset.cart && <Badge>Stays with its cart</Badge>}
                      </Td>
                      <Td className="text-right">
                        <button
                          onClick={() => setScanned((current) => current.filter((row) => row.id !== asset.id))}
                          className="text-slate-400 hover:text-red-600"
                          aria-label={`Remove ${asset.assetTag}`}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {scanned.length > 0 && (
              <div className="flex justify-end border-t border-slate-100 p-4">
                <Button variant="primary" icon={<ClipboardCheck />} onClick={() => setConfirming(true)}>
                  Verify {scanned.length} asset{scanned.length === 1 ? '' : 's'}
                </Button>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {result && (
            <Alert tone="green" icon={<CircleCheck />} title={`Verified ${devices(result.verified)} in ${result.label}`}>
              <ul className="mt-1 space-y-0.5">
                {result.moved > 0 && <li>{result.moved} now kept there.</li>}
                {result.onCarts > 0 && <li>{cartNote(result.onCarts, 'stayed')}.</li>}
                {result.workOrder && result.nextVerificationOn && (
                  <li>
                    <Link to={`/work-orders/${result.workOrder.id}`} className="font-medium underline">
                      Ticket #{result.workOrder.number}
                    </Link>{' '}
                    checks this room again on {formatUtcDate(`${result.nextVerificationOn}T00:00:00.000Z`)}.
                  </li>
                )}
              </ul>
            </Alert>
          )}

          <Card>
            <CardHeader title="This room" description={place.data?.label ?? 'Choose a building and room'} />
            <div className="space-y-3 p-5 text-sm">
              {!chosen ? (
                <p className="text-slate-500">Pick where you are to see what Edvora expects to find there.</p>
              ) : place.isLoading ? (
                <Spinner />
              ) : place.error || !place.data ? (
                <p className="text-amber-700">{errorMessage(place.error)}</p>
              ) : (
                <>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-slate-500">Kept here in Edvora</span>
                    <span className="text-lg font-semibold text-slate-900">{place.data.expected}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-slate-500">Scanned so far</span>
                    <span className="text-lg font-semibold text-slate-900">{scanned.length}</span>
                  </div>
                  {place.data.lastVerified ? (
                    <p className="border-t border-slate-100 pt-3 text-xs text-slate-500">
                      Last verified {formatRelative(place.data.lastVerified.verifiedAt)} by {place.data.lastVerified.verifiedByName} ·{' '}
                      {devices(place.data.lastVerified.devices)}
                      {place.data.lastVerified.workOrder && (
                        <>
                          {' · next check '}
                          <Link to={`/work-orders/${place.data.lastVerified.workOrder.id}`} className="inline-flex items-center gap-1 text-brand-700 hover:underline">
                            #{place.data.lastVerified.workOrder.number} <ExternalLink className="size-3" />
                          </Link>
                        </>
                      )}
                    </p>
                  ) : (
                    <p className="border-t border-slate-100 pt-3 text-xs text-slate-500">Never verified.</p>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        size="sm"
        title={`Verify ${scanned.length} asset${scanned.length === 1 ? '' : 's'}`}
        description={place.data?.label}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="primary" icon={<ClipboardCheck />} loading={verify.isPending} onClick={() => verify.mutate()}>
              Verify
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Each device is recorded as verified here{willMove > 0 ? `, and ${willMove} of them will be kept here from now on` : ''}.
            {onCarts > 0 && ` ${cartNote(onCarts, 'stay')}.`}
          </p>
          <Field label="Verify this room again on" hint="Optional. Opens a ticket for this room on that day.">
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={nextOn} min={localToday()} onChange={(event) => setNextOn(event.target.value)} className="w-44" />
              {nextOn && (
                <Button size="sm" variant="ghost" icon={<CalendarClock />} onClick={() => setNextOn('')}>
                  Don’t schedule
                </Button>
              )}
            </div>
          </Field>
          <Field label="Notes" hint="Kept with each device’s verification record">
            <Textarea rows={2} value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="Anything worth remembering about this sweep" />
          </Field>
        </div>
      </Modal>
    </>
  );
}
